<script setup lang="ts">
import router from '@/router'

import AudienceRulesPage from './AudienceRulesPage.vue'
import { AUDIENCE_TEMPLATES_CONFIG } from './config/templates.config'

import type { AudienceRule, Rules } from '@/types/types'
import { useContentScopeEntry } from '@/composables/contentScope'
import { isContentScope } from '@/utils/contentScope'

const { t } = useI18n()
const { createAudience } = useAudience()
const { send } = useEvent()
const route = useRoute()
const notification = useNotification()

const segmentName = computed(() => route.query.name as string)
const fromMarketingFlow = computed(() => route.query.fromMarketingFlow as string | undefined)
const storeIds = ref<number[]>([])
const { scope, isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry(
  ref(isContentScope(route.query.scope) ? route.query.scope : null)
)

const createLoading = ref(false)
const createModal = ref(false)
const errorMessage = ref('')
const initialRules = ref<Record<Rules, AudienceRule> | undefined>(undefined)
const rewriteSegmentName = ref('')
const rules = ref<Record<string, any>>({})
const segmentWasCreated = ref(false)
const templateType = ref(route.query.template ? (route.query.template as string) : null)

const hasRulesError = ref(false)

const rulesEmpty = computed(() => {
  return !Object.values(rules.value).some((rule) => rule.value.length > 0)
})

const hasNoCondition = computed(() => rulesEmpty.value && storeIds.value.length === 0)

const segment = computed(() => ({
  name: segmentName.value,
  type: 'dynamic',
  ...(templateType.value && { template_used: templateType.value }),
  ...(storeIds.value.length && { store_ids: storeIds.value }),
  ...(scope.value && { scope: scope.value })
}))

const handleRulesUpdate = (newRules: Record<string, any>) => {
  Object.assign(rules.value, newRules)
}

const getModifiedAttributes = () => {
  if (!initialRules.value || !templateType.value) {
    return []
  }

  const modifiedAttributes: string[] = []

  Object.keys(rules.value).forEach((key) => {
    const currentRule = rules.value[key]
    const initialRule = initialRules.value?.[key]

    if (!initialRule) return

    const currentValue = JSON.stringify(currentRule.value)
    const initialValue = JSON.stringify(initialRule.value)

    const currentOperator = currentRule.operator
    const initialOperator = initialRule.operator

    const currentParams = currentRule.parameters ? JSON.stringify(currentRule.parameters) : null
    const initialParams = initialRule.parameters ? JSON.stringify(initialRule.parameters) : null

    if (currentValue !== initialValue || currentOperator !== initialOperator || currentParams !== initialParams) {
      modifiedAttributes.push(key)
    }
  })

  return modifiedAttributes
}

const createSegment = async () => {
  createLoading.value = true

  try {
    const segmentId = await createAudience({
      audience: { ...segment.value, name: rewriteSegmentName.value },
      rules: rules.value,
      sendNotification: false
    })

    segmentWasCreated.value = true

    const modifiedAttributes = getModifiedAttributes()
    send('segment', 'segment.created', {
      template_id: templateType.value || null,
      modified_attributes: modifiedAttributes,
      modified_attributes_count: modifiedAttributes.length
    })

    if (fromMarketingFlow.value) {
      router.push({
        name: 'MarketingFlowTemplates',
        query: {
          fromAudienceCreate: 'true',
          audienceId: segmentId.toString(),
          marketingFlowTemplate: fromMarketingFlow.value
        }
      })
    } else {
      router.push({ name: 'AudienceDetail', params: { audienceId: segmentId }, query: { activeTab: 'rules' } })
    }
  } catch (err) {
    errorMessage.value = err.message
    // The error modal only exists for the template flow: without a template,
    // nothing else renders errorMessage, so the failure would be silent.
    if (!templateType.value) {
      notification.create('error', { text: err.message, timeout: 3000 })
    }
  } finally {
    createLoading.value = false
  }
}

const saveSegment = async () => {
  if (templateType.value) {
    createModal.value = true
  } else {
    await createSegment()
  }
}

const handleRulesError = (newHasRulesError: boolean) => {
  hasRulesError.value = newHasRulesError
}

onMounted(() => {
  if (!segmentName.value) {
    router.push({ name: 'AudienceList' })
    return
  }

  rewriteSegmentName.value = segmentName.value
  askScope()

  if (templateType.value) {
    const template = AUDIENCE_TEMPLATES_CONFIG.find((t) => t.type === templateType.value)
    if (template) {
      initialRules.value = template.rules
    }
  }
})

onUnmounted(() => {
  if (!segmentWasCreated.value) {
    send('segment', 'segment.closed', {
      template_id: templateType.value
    })
  }
})

watch(
  () => rewriteSegmentName.value,
  () => {
    if (errorMessage.value) {
      errorMessage.value = ''
    }
  }
)
</script>

<template>
  <section>
    <TheDetailHeader
      :title="segmentName"
      :parent="{ label: t('pages.customer.audiences.header.crumbs'), to: { name: 'AudienceList' } }"
    >
      <template #actions>
        <Button variant="default-primary" :disabled="hasNoCondition || hasRulesError" @click="saveSegment">
          {{ t('pages.customer.audiences.create.dynamic.btn') }}
        </Button>
      </template>
    </TheDetailHeader>
    <div class="px-6 sm:px-12">
      <AudienceRulesPage
        :is-update="false"
        is-editing
        :initial-rules="initialRules"
        :initial-store-ids="storeIds"
        @has-rules-error="handleRulesError"
        @update-rules="handleRulesUpdate"
        @update:store-ids="storeIds = $event"
      />
    </div>

    <BaseModal
      :is-open="createModal"
      :heading-title="t('pages.customer.audiences.create.dynamic.modal.title', { name: segmentName })"
      size="2xl"
      :link-text="t('pages.customer.audiences.create.dynamic.modal.btn')"
      :is-processing="createLoading"
      :cancel="t('pages.customer.audiences.create.dynamic.modal.btnCancel')"
      @click="createSegment"
      @close="createModal = false"
    >
      <div class="flex flex-col">
        <label for="segment" class="mb-1.5 text-sm font-medium text-foreground">
          {{ t('pages.customer.audiences.create.dynamic.modal.label') }}
        </label>
        <AppFormInput id="segment" v-model="rewriteSegmentName" type="text" name="recordingName" />
        <div class="pt-1">
          <p v-if="errorMessage" class="text-xs text-red-400">{{ errorMessage }}</p>
        </div>
      </div>
    </BaseModal>

    <ContentScopeModal
      v-model:open="isScopeModalOpen"
      subject="audience"
      @confirm="confirmScope"
      @cancel="router.push({ name: 'AudienceList' })"
    />
  </section>
</template>
